// Generic evidence-span client utilities.
// Backend contract lives in /app/backend/services/evidence_spans.py.

/**
 * Find a span by (source, source_key, kind).
 * All three must match; the first match wins.
 */
export function findSpan(spans, { source, source_key, kind } = {}) {
  if (!Array.isArray(spans)) return null;
  return (
    spans.find(
      (s) =>
        (!source || s.source === source) &&
        (!source_key || s.source_key === source_key) &&
        (!kind || s.kind === kind)
    ) || null
  );
}

/**
 * Return every span that targets the given paragraph index.
 */
export function spansForParagraph(spans, paragraphIndex) {
  if (!Array.isArray(spans)) return [];
  return spans.filter((s) => s.paragraph_hint === paragraphIndex);
}

/**
 * Scroll the matching <mark id={spanId}> into view and briefly flash it.
 * No-op when the span is missing from the DOM (document unmounted etc.).
 */
export function focusSpan(spanId) {
  if (!spanId || typeof document === "undefined") return;
  const el = document.getElementById(spanId);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.setAttribute("data-evidence-flash", "true");
  window.setTimeout(() => el.removeAttribute("data-evidence-flash"), 1800);
}

/**
 * Render a paragraph with any evidence spans wrapped in <mark>.
 * Returns an array of React-compatible children.
 *
 * We walk through the paragraph greedily — longest quotes first — so that
 * nested highlights (e.g. a change_quote inside the broader evidence quote)
 * do not swallow each other.
 */
export function renderWithHighlights(paragraphText, spans, React) {
  const text = paragraphText || "";
  const applicable = (spans || [])
    .filter((s) => s && s.quote)
    .map((s) => ({ ...s, _lc: s.quote.toLowerCase() }))
    .sort((a, b) => b.quote.length - a.quote.length);

  if (applicable.length === 0) return [text];

  // Build a list of {start, end, span} by scanning the lower-case text.
  const lc = text.toLowerCase();
  const used = new Array(text.length).fill(false);
  const hits = [];
  for (const span of applicable) {
    const needle = span._lc;
    if (!needle) continue;
    const idx = lc.indexOf(needle);
    if (idx === -1) continue;
    // Skip if the range overlaps an already-used shorter mark.
    let overlap = false;
    for (let i = idx; i < idx + needle.length; i++) {
      if (used[i]) {
        overlap = true;
        break;
      }
    }
    if (overlap) continue;
    for (let i = idx; i < idx + needle.length; i++) used[i] = true;
    hits.push({ start: idx, end: idx + needle.length, span });
  }

  if (hits.length === 0) return [text];
  hits.sort((a, b) => a.start - b.start);

  const children = [];
  let cursor = 0;
  hits.forEach((h, i) => {
    if (h.start > cursor) children.push(text.slice(cursor, h.start));
    const slice = text.slice(h.start, h.end);
    children.push(
      React.createElement(
        "mark",
        {
          key: `mark-${h.span.id}-${i}`,
          id: h.span.id,
          "data-testid": `evidence-mark-${h.span.id}`,
          "data-evidence-source": h.span.source,
          "data-evidence-kind": h.span.kind,
          title: h.span.label,
          style: {
            backgroundColor: `${h.span.color}22`,
            borderBottom: `2px solid ${h.span.color}`,
            padding: "0 1px",
            borderRadius: "2px",
            scrollMarginTop: "90px",
          },
        },
        slice
      )
    );
    cursor = h.end;
  });
  if (cursor < text.length) children.push(text.slice(cursor));
  return children;
}
